'use client'

import { Button, useDocumentInfo } from '@payloadcms/ui'
import { useRouter } from 'next/navigation'
import React, { useEffect, useState } from 'react'

const ViewStudentRecordButton: React.FC = () => {
  const { id, docPermissions } = useDocumentInfo()
  const router = useRouter()
  const [studentId, setStudentId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStudentRecord = async () => {
      if (!id) {
        setLoading(false)
        return
      }

      try {
        const response = await fetch(`/api/students?where[user][equals]=${id}&limit=1`, {
          credentials: 'include',
        })

        if (!response.ok) {
          throw new Error('Failed to fetch student record')
        }

        const data = await response.json()

        if (data.docs && data.docs.length > 0) {
          setStudentId(data.docs[0].id)
        } else {
          setError('No student record found')
        }
      } catch (err) {
        console.error('Error fetching student record:', err)
        setError('Error loading student record')
      } finally {
        setLoading(false)
      }
    }

    fetchStudentRecord()
  }, [id])

  const handleClick = () => {
    if (studentId) {
      router.push(`/admin/collections/students/${studentId}`)
    }
  }

  // Show loading state
  if (loading) {
    return (
      <div className="my-4 p-3 rounded bg-[var(--theme-elevation-50)] border border-[var(--theme-elevation-100)]">
        <p className="text-sm text-[var(--theme-elevation-600)] m-0">Loading student record...</p>
      </div>
    )
  }

  // Show message if document is not saved yet
  if (!id) {
    return (
      <div className="my-4 p-3 rounded bg-[var(--theme-elevation-50)] border border-[var(--theme-elevation-100)]">
        <p className="text-sm text-[var(--theme-elevation-600)] m-0">
          💡 Save the user first to view student record
        </p>
      </div>
    )
  }

  // Show error or no record found
  if (error || !studentId) {
    return (
      <div className="my-4 p-3 rounded bg-[var(--theme-warning-50)] border border-[var(--theme-warning-200)]">
        <p className="text-sm text-[var(--theme-warning-600)] m-0">
          ℹ️ {error || 'No student record found for this user'}
        </p>
      </div>
    )
  }

  return (
    <Button buttonStyle="primary" size="large" className="mt-0" onClick={handleClick}>
      View Student Record
    </Button>
  )
}

export default ViewStudentRecordButton
